import FeatureType from '../../models/FeatureType';
import { MarkerType } from 'reactflow';
import {
  getSlotNameFromNodeName,
  machineInterpretableToHumanReadable,
  resolveInitialNodeName,
} from '../../components/Dialog/slotNameTransformer';

export const structuredClone = (obj) => JSON.parse(JSON.stringify(obj));

const typeMap = {
  StartNode: 'startNode',
  QuestionNode: 'questionNode',
  TopicChoiceNode: 'topicChoiceNode',
  AussageNode: 'aussageNode',
  EndeNode: 'endeNode',
  RegexChoiceNode: 'regexChoiceNode',
  NumberChoiceNode: 'numberChoiceNode',
  TextChoiceNode: 'textChoiceNode',
  defaultNode: 'defaultNode',
  apiActionNode: 'apiActionNode',
  faasChoiceNode: 'faasChoiceNode',
  dateChoiceNode: 'dateChoiceNode',
  emailActionNode: 'emailActionNode',
  phoneForwardingActionNode: 'phoneForwardingActionNode',
  userInputNode: 'userInputNode',
  addNode: 'addNode',
};

export const setInitialNodeNamesAndSlotNames = (nodes, dialogID, dialogName) => {
  const nodeNameNodes = nodes.map((node) => {
    const {
      slotName,
      featureType,
    } = node.data;
    let { nodeName } = node.data;
    if (!nodeName && slotName) {
      nodeName = machineInterpretableToHumanReadable(slotName, dialogName);
    } else {
      nodeName = resolveInitialNodeName(nodeName, featureType);
    }
    return {
      ...node,
      data: {
        ...node.data,
        nodeName,
      },
    };
  });
  const slotNameNodes = nodeNameNodes.map((node) => {
    const slotName = getSlotNameFromNodeName(dialogID, dialogName, nodeNameNodes, node);
    return {
      ...node,
      data: {
        ...node.data,
        slotName,
      },
    };
  });
  return slotNameNodes;
};

export const getIncomingAndOutgoingEdges = (nodeId, edges) => {
  // Step 1: Find all addNodes and their incoming edges
  const incomingEdges = edges.filter((edge) => edge.target === nodeId);
  const outgoingEdges = edges.filter((edge) => edge.source === nodeId);

  return [incomingEdges, outgoingEdges];
};

const addMissingEdgeHandler = (edge) => {
  const copyEdge = structuredClone(edge);
  const { sourceHandle, targetHandle } = copyEdge;
  if (!sourceHandle) {
    copyEdge.sourceHandle = 'defaultSource';
  }
  if (!targetHandle) {
    copyEdge.targetHandle = 'defaultTarget';
  }
  return copyEdge;
};

export const translateNodesAndEdgesToFeatureFormat = (nodes, edges) => {
  const mappedNodes = [];
  if (nodes) {
    nodes.forEach((node) => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
      // @ts-ignore
      // eslint-disable-next-line no-param-reassign
      node.type = (node.type in typeMap) ? typeMap[node.type] : node.type;
      switch (node.type) {
        case 'addNode':
          node.data.featureType = FeatureType.ADD_NODE;
          break;
        case 'phoneForwardingActionNode':
          node.data.featureType = FeatureType.PHONE_FORWARDING_ACTION_NODE;
          break;
        case 'startNode':
          node.data.featureType = FeatureType.START_NODE;
          break;
        case 'questionNode':
          node.data.featureType = FeatureType.QUESTION_NODE;
          break;
        case 'topicChoiceNode':
          node.data.featureType = FeatureType.TOPIC_CHOICE_NODE;
          break;
        case 'aussageNode':
          node.data.featureType = FeatureType.UTTER_NODE;
          break;
        case 'endeNode':
          node.data.featureType = FeatureType.END_NODE;
          break;
        case 'regexChoiceNode':
          node.data.featureType = FeatureType.REGEX_CHOICE_NODE;
          break;
        case 'numberChoiceNode':
          node.data.featureType = FeatureType.NUMBER_CHOICE_NODE;
          break;
        case 'textChoiceNode':
          node.data.featureType = FeatureType.TEXT_CHOICE_NODE;
          break;
        case 'apiActionNode':
          node.data.featureType = FeatureType.API_ACTION_NODE;
          break;
        case 'faasChoiceNode':
          node.data.featureType = FeatureType.FAAS_CHOICE_NODE;
          break;
        case 'dateChoiceNode':
          node.data.featureType = FeatureType.DATE_CHOICE_NODE;
          break;
        case 'timeDecisionNode':
          node.data.featureType = FeatureType.TIME_DECISION_NODE;
          break;
        case 'emailActionNode':
          node.data.featureType = FeatureType.EMAIL_ACTION_NODE;
          break;
        case 'userInputNode':
          node.data.featureType = FeatureType.USER_INPUT_NODE;
          break;
      }
      node.data.featureID = node.id;

      mappedNodes.push(node);
    });
  }

  const mappedEdges = [];
  if (edges) {
    edges.forEach((edge) => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
      // @ts-ignore
      // eslint-disable-next-line no-param-reassign
      edge.markerEnd = {
        type: MarkerType.ArrowClosed,
      };
      edge.type = 'step';
      edge = addMissingEdgeHandler(edge);
      mappedEdges.push(edge);
    });
  }
  return [mappedNodes, mappedEdges];
};

export const translateNodesAndEdgesToLegacyFormat = (nodes, edges) => {
  const invertedTypeMap = Object.entries(typeMap).reduce((acc, [key, value]) => {
    acc[value] = key;
    return acc;
  }, {});

  const mappedNds = [];
  if (nodes) {
    nodes.forEach((node) => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
      // @ts-ignore
      // eslint-disable-next-line no-param-reassign
      node.type = (node.type in invertedTypeMap) ? invertedTypeMap[node.type] : node.type;
      mappedNds.push(node);
    });
  }

  return [mappedNds, edges];
};

export const addIncomingOutgoingEdges = (dialogs) => Object.fromEntries(
  Object.entries(dialogs).map(
    ([dialogID, dialog]) => {
      const { graph } = dialog || {};
      let { nodes } = graph || {};
      nodes = nodes?.map((node) => {
        const resultNode = node;
        const [incomingEdges, outgoingEdges] = getIncomingAndOutgoingEdges(
          node.id, dialog.graph.edges,
        );
        resultNode.data.incomingEdges = incomingEdges;
        resultNode.data.outgoingEdges = outgoingEdges;
        return resultNode;
      });
      return [dialogID, {
        ...dialog,
        graph: {
          ...graph,
          nodes,
        },
      }];
    },
  ),
);

export const mergeNewDialogsAndCustomDialogs = (
  dialogs,
  dbDialogs,
) => {
  const mergedDialogs = dialogs;
  dbDialogs.forEach((dialog) => {
    if (!dialogs[dialog.id]) {
      mergedDialogs[dialog.id] = dialog;
    } else {
      mergedDialogs[dialog.id].phonenumbers = dialog.phonenumbers;
      mergedDialogs[dialog.id].graphsCount = dialog?.dialog_graphs_aggregate?.aggregate?.count;
    }
  });
  return mergedDialogs;
};
